import { useState, useEffect, useCallback, useMemo } from "react";
import {
  ArrowSquareOut,
  CheckCircle,
  Clock,
  PaperPlaneTilt,
  PencilSimple,
  Sparkle,
} from "@phosphor-icons/react";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "sonner";
import { fetchDecisionQuestions, submitOwnerDecision } from "../lib/api";

/**
 * CardDecisionFooter — canonical primitive for Owner per-card decisions.
 *
 * 2026-05-23 cycle-5c+25-fix3 rewrite (per Owner correction):
 *   Each decidable roadmap card fetches its OWN questions from canon via
 *   `GET /api/owner/decision-questions/<item_type>/<item_id>`. If the endpoint
 *   returns 404 (no authored questions for this card yet), the footer renders
 *   nothing at all (Owner cannot decide on un-authored cards — by design).
 *
 *   For each question:
 *     - MP's options are rendered as bullet-style cards (a)/(b)/(c)/(d).
 *     - MP's recommendation paragraph renders inline below the options.
 *     - Pick widget renders INLINE per-question after the rec — radio if
 *       `pick_type=="radio"`, checkbox if `pick_type=="checkbox"`.
 *     - A per-question free-text textarea sits below the pick widget for
 *       optional clarification.
 *   A single "Commit decision" button at the bottom of the card submits all
 *   picks + per-question notes + the cross-question notes.
 *
 * Backend contract: POST /api/owner/decide with
 *   {item_id, item_type, item_title, source_path,
 *    answers: [{question_number, question_title, pick_type,
 *               letters: [...], letter_labels: [...], free_text}, ...],
 *    cross_question_notes: "..."}
 *
 * See ADR-MP-003 §5-v2 for the canon contract.
 */

const GITHUB_REPO_BASE = "https://github.com/lifttechanz/lifttech-shared/blob/main";

function buildDeepLink(sourcePath) {
  if (!sourcePath) return null;
  const cleaned = sourcePath.replace(/^\/+/, "");
  return `${GITHUB_REPO_BASE}/${cleaned}`;
}

/**
 * Minimal markdown inline renderer — handles **bold** (and nothing else) so
 * the §1.Q card-level description renders cleanly with bolded section labels
 * ("What this is", "Why this needs you", etc.). Authored cycle-5c+25-fix13
 * per Owner — "i think the asterisks are supposed to make the question bold".
 */
function renderInlineBold(text) {
  if (!text) return null;
  const parts = [];
  const re = /\*\*([^*]+)\*\*/g;
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    parts.push(<strong key={key++}>{m[1]}</strong>);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts;
}

function LastDecisionBadge({ decision }) {
  if (!decision) return null;
  const colour = "hsl(var(--primary))";
  const summary =
    (decision.answer_summary && decision.answer_summary.trim()) ||
    (decision.choice ? decision.choice.toUpperCase() : "(unknown)");
  const tooltipText = `Last decision on ${decision.submitted_at}: ${summary}${decision.free_text ? " — " + decision.free_text.slice(0, 120) : ""}`;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-mono"
      style={{
        fontSize: "0.6rem",
        fontWeight: 700,
        color: colour,
        background: "hsl(var(--primary) / 0.08)",
        borderColor: "hsl(var(--primary) / 0.4)",
      }}
      title={tooltipText}
      data-testid="card-last-decision-badge"
    >
      <CheckCircle size={9} />
      Last: {summary} · {decision.submitted_at}
    </span>
  );
}

/**
 * One question block — render structure (cycle-5c+25-fix4 per Owner direction):
 *   1. Question number + title
 *   2. INTERACTIVE option list — each option card has its radio/checkbox
 *      directly on it (clicking the card = picking that option). No
 *      separate "Your pick" widget below.
 *   3. MP recommendation rationale paragraph (read-only)
 *   4. Per-question note textarea (optional clarification)
 */
function QuestionBlock({ question, qState, onTogglePick, onFreeText, itemId }) {
  const isCheckbox = question.pick_type === "checkbox";
  const picks = qState.letters || [];
  const answered = picks.length > 0 || Boolean((qState.free_text || "").trim());

  return (
    <div
      className="space-y-2"
      data-testid={`card-decision-question-${itemId}-${question.number}`}
    >
      <div className="flex items-baseline gap-2 mb-1">
        <span
          className="text-mono font-semibold"
          style={{ fontSize: "0.85rem", color: "hsl(var(--primary))" }}
        >
          {question.number}
        </span>
        <span
          className="text-sm font-medium"
          style={{ color: "hsl(var(--foreground))" }}
        >
          {question.title}
        </span>
        <span
          className="text-mono ml-2"
          style={{
            fontSize: "0.55rem",
            color: "hsl(var(--muted-foreground))",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            fontWeight: 600,
          }}
        >
          {isCheckbox ? "tick all that apply" : "pick one"}
        </span>
        {answered && (
          <span
            className="inline-flex items-center gap-0.5 rounded-md border px-1.5 py-0.5 ml-auto"
            style={{
              fontSize: "0.55rem",
              fontWeight: 600,
              background: "hsl(var(--status-success) / 0.1)",
              color: "hsl(var(--status-success))",
              borderColor: "hsl(var(--status-success) / 0.4)",
            }}
            data-testid={`card-decision-answered-${itemId}-${question.number}`}
          >
            <CheckCircle size={9} weight="bold" />
            Answered
          </span>
        )}
      </div>

      {/* Question description / context paragraph(s) (if MP authored any) */}
      {question.description && question.description.trim() && (
        <div
          className="text-xs leading-relaxed border-l-2 pl-3 py-1"
          style={{
            color: "hsl(var(--muted-foreground))",
            borderColor: "hsl(var(--primary) / 0.35)",
            background: "hsl(var(--muted) / 0.3)",
            whiteSpace: "pre-wrap",
          }}
          data-testid={`card-decision-description-${itemId}-${question.number}`}
        >
          {question.description}
        </div>
      )}

      {/* Interactive option list — input lives ON each option card */}
      <div className="space-y-1.5">
        {question.options.map((opt) => {
          const checked = picks.includes(opt.letter);
          return (
            <label
              key={opt.letter}
              className="flex items-start gap-2 rounded-md border px-2 py-1.5 cursor-pointer transition-colors"
              style={{
                borderColor: checked
                  ? "hsl(var(--primary) / 0.7)"
                  : opt.is_mp_rec
                    ? "hsl(var(--primary) / 0.35)"
                    : "hsl(var(--border))",
                background: checked
                  ? "hsl(var(--primary) / 0.10)"
                  : opt.is_mp_rec
                    ? "hsl(var(--primary) / 0.04)"
                    : "transparent",
              }}
              onClick={(e) => e.stopPropagation()}
              data-testid={`card-decision-pick-${itemId}-${question.number}-${opt.letter}`}
            >
              <input
                type={isCheckbox ? "checkbox" : "radio"}
                name={`pick-${itemId}-${question.number}`}
                value={opt.letter}
                checked={checked}
                onChange={() => onTogglePick(opt.letter)}
                className="mt-0.5 shrink-0"
              />
              <span
                className="text-mono font-bold shrink-0"
                style={{
                  fontSize: "0.7rem",
                  color: checked
                    ? "hsl(var(--primary))"
                    : opt.is_mp_rec
                      ? "hsl(var(--primary))"
                      : "hsl(var(--muted-foreground))",
                  minWidth: "1.4rem",
                }}
              >
                ({opt.letter})
              </span>
              <p
                className="text-xs leading-snug flex-1"
                style={{
                  color: "hsl(var(--foreground))",
                  fontWeight: checked ? 600 : 400,
                }}
              >
                {opt.summary}
              </p>
              {opt.is_mp_rec && (
                <span
                  className="inline-flex items-center gap-0.5 rounded-md border px-1 py-0.5 text-mono shrink-0"
                  style={{
                    fontSize: "0.55rem",
                    fontWeight: 600,
                    background: "hsl(var(--primary) / 0.12)",
                    color: "hsl(var(--primary))",
                    borderColor: "hsl(var(--primary) / 0.4)",
                  }}
                >
                  <Sparkle size={8} weight="fill" />
                  MP rec
                </span>
              )}
            </label>
          );
        })}
      </div>

      {/* MP's recommendation rationale (if present) */}
      {question.mp_rec_rationale && (
        <p
          className="text-xs leading-relaxed pl-1"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          <span
            className="text-mono"
            style={{
              fontSize: "0.6rem",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 700,
              color: "hsl(var(--primary))",
              marginRight: "0.4rem",
            }}
          >
            My rec:
          </span>
          {question.mp_rec_rationale}
        </p>
      )}

      {/* Per-question note textarea — no separate container */}
      <div className="pt-1">
        <div className="flex items-center gap-1 mb-0.5">
          <PencilSimple
            size={10}
            style={{ color: "hsl(var(--muted-foreground))" }}
          />
          <label
            className="text-mono"
            style={{
              fontSize: "0.6rem",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 600,
              color: "hsl(var(--muted-foreground))",
            }}
          >
            Add a note on {question.number} (optional)
          </label>
        </div>
        <textarea
          value={qState.free_text || ""}
          onChange={(e) => onFreeText(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          rows={2}
          placeholder={`Clarify, push back, or write your own answer to ${question.number}.`}
          className="w-full rounded-md border px-2 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{
            fontSize: "0.75rem",
            background: "hsl(var(--background))",
            borderColor: (qState.free_text || "").trim()
              ? "hsl(var(--primary) / 0.4)"
              : "hsl(var(--border))",
            color: "hsl(var(--foreground))",
            resize: "vertical",
          }}
          data-testid={`card-decision-freetext-${itemId}-${question.number}`}
        />
      </div>
    </div>
  );
}

export default function CardDecisionFooter({
  itemId,
  itemType,
  itemTitle,
  sourcePath = "",
  lastDecision = null,
  onDecisionSubmitted = null,
}) {
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const [questionsData, setQuestionsData] = useState(null);
  const [loadState, setLoadState] = useState("idle"); // idle | loading | ok | absent | error
  const [loadError, setLoadError] = useState("");
  const [picks, setPicks] = useState({});
  const [crossQuestionNotes, setCrossQuestionNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const deepLink = buildDeepLink(sourcePath);

  // Fetch decision questions for this card on mount / when key inputs change.
  useEffect(() => {
    let cancelled = false;
    setLoadState("loading");
    setLoadError("");
    setQuestionsData(null);
    fetchDecisionQuestions(itemType, itemId, sourcePath)
      .then((data) => {
        if (cancelled) return;
        if (data && data.questions && data.questions.length > 0) {
          setQuestionsData(data);
          setLoadState("ok");
        } else {
          setLoadState("absent");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404 || status === 400) {
          // 404 = no §1.Q sub-block authored yet (by design — hide footer)
          // 400 = unknown item_type (hide for safety)
          setLoadState("absent");
        } else {
          setLoadState("error");
          setLoadError(err?.message || String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [itemType, itemId, sourcePath]);

  const questions = questionsData?.questions || [];

  const isAnswered = useCallback(
    (qNum) => {
      const v = picks[qNum];
      if (!v) return false;
      return (v.letters && v.letters.length > 0) || Boolean((v.free_text || "").trim());
    },
    [picks],
  );

  const anyAnswered = useMemo(
    () => questions.some((q) => isAnswered(q.number)),
    [questions, isAnswered],
  );
  const allAnswered = useMemo(
    () => questions.length > 0 && questions.every((q) => isAnswered(q.number)),
    [questions, isAnswered],
  );

  const togglePick = (qNum, letter, isCheckbox) => {
    setPicks((prev) => {
      const existing = prev[qNum] || { letters: [], free_text: "" };
      const current = existing.letters || [];
      let nextLetters;
      if (isCheckbox) {
        nextLetters = current.includes(letter)
          ? current.filter((l) => l !== letter)
          : [...current, letter].sort();
      } else {
        // radio: clicking the already-selected letter clears (toggle off)
        nextLetters = current[0] === letter ? [] : [letter];
      }
      return { ...prev, [qNum]: { ...existing, letters: nextLetters } };
    });
  };

  const setFreeTextFor = (qNum, text) => {
    setPicks((prev) => {
      const existing = prev[qNum] || { letters: [], free_text: "" };
      return { ...prev, [qNum]: { ...existing, free_text: text } };
    });
  };

  const buildAnswersPayload = () => {
    const out = [];
    for (const q of questions) {
      const v = picks[q.number];
      if (!v) continue;
      const letters = v.letters || [];
      const freeText = (v.free_text || "").trim();
      if (letters.length === 0 && !freeText) continue;
      const letterLabels = letters.map(
        (l) => (q.options.find((o) => o.letter === l)?.summary || "").slice(0, 240),
      );
      out.push({
        question_number: q.number,
        question_title: q.title,
        pick_type: q.pick_type || "radio",
        letters,
        letter_labels: letterLabels,
        free_text: freeText,
      });
    }
    return out;
  };

  const canSubmit = isAuthenticated && anyAnswered && !submitting && loadState === "ok";

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Fetch the raw ID token (Auth0 ID-token mode; see /app/backend/auth.py
      // which verifies against audience = SPA client ID). Access tokens have
      // a different audience and will fail JWKS validation.
      const claims = await getIdTokenClaims();
      const token = claims?.__raw;
      if (!token) {
        toast.error("Couldn't read your sign-in token", {
          description: "Try refreshing the page or signing in again.",
        });
        setSubmitting(false);
        return;
      }
      const result = await submitOwnerDecision(
        {
          item_id: itemId,
          item_type: itemType,
          item_title: itemTitle,
          source_path: sourcePath,
          answers: buildAnswersPayload(),
          cross_question_notes: crossQuestionNotes,
          dry_run: false,
        },
        token,
      );
      if (result?.pushed) {
        toast.success("Decision committed to canon", {
          description: result.target_path,
        });
      } else if (result?.commit_sha) {
        toast.warning("Decision committed locally but push failed", {
          description: result.push_error || "see backend logs",
        });
      } else {
        toast.error("Decision submission failed", {
          description: result?.error || "unknown error",
        });
      }
      setPicks({});
      setCrossQuestionNotes("");
      if (onDecisionSubmitted) onDecisionSubmitted(result);
    } catch (err) {
      console.error("submit decision error", err);
      toast.error("Failed to submit decision", {
        description: err?.response?.data?.detail || err.message || String(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = (e) => {
    if (e) e.stopPropagation();
    setPicks({});
    setCrossQuestionNotes("");
  };

  // ── Render gates ───────────────────────────────────────────────────────────

  // Hide entirely on cards that have no authored questions OR while still
  // loading the lookup. Per cycle-5c+25-fix3 contract: cards without authored
  // questions render NO decision footer.
  if (loadState === "idle" || loadState === "loading" || loadState === "absent") {
    return null;
  }

  if (loadState === "error") {
    return (
      <div
        className="mt-3 pt-3 border-t text-xs"
        style={{
          borderColor: "hsl(var(--border) / 0.6)",
          color: "hsl(var(--status-critical))",
        }}
        data-testid={`card-decision-footer-error-${itemId}`}
        onClick={(e) => e.stopPropagation()}
      >
        Failed to load decision questions: {loadError}
      </div>
    );
  }

  // ── Authored card — render the full multi-question form ──────────────────
  const answeredCount = questions.filter((q) => isAnswered(q.number)).length;

  return (
    <div
      className="mt-3 pt-3 border-t"
      style={{ borderColor: "hsl(var(--border) / 0.6)" }}
      data-testid={`card-decision-footer-${itemId}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="text-mono"
            style={{
              fontSize: "0.6rem",
              color: "hsl(var(--muted-foreground))",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 600,
            }}
          >
            Your decision · {questions.length}{" "}
            {questions.length === 1 ? "question" : "questions"}
          </span>
          <LastDecisionBadge decision={lastDecision} />
        </div>
        {deepLink && (
          <a
            href={deepLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-mono transition-colors hover:text-[hsl(var(--primary))]"
            style={{ fontSize: "0.65rem", color: "hsl(var(--muted-foreground))" }}
            data-testid={`card-readmore-${itemId}`}
            onClick={(e) => e.stopPropagation()}
          >
            <ArrowSquareOut size={10} />
            Read more
          </a>
        )}
      </div>

      <div
        className="space-y-5 mb-4"
        data-testid={`card-decision-questions-${itemId}`}
      >
        {/* Card-level plain-language description (cycle-5c+25-fix12).
            Authored in §1.Q sub-block between `### <row-id>` and the first
            `#### Q1`. Explains what the card is, why it's waiting on the
            Owner, what it gates, and how much time it takes. Rendered as
            a tinted callout panel above all question blocks. */}
        {questionsData?.card_description && questionsData.card_description.trim() && (
          <div
            className="rounded-md border p-2.5 sm:p-3 space-y-2"
            style={{
              borderColor: "hsl(var(--primary) / 0.35)",
              background: "hsl(var(--primary) / 0.04)",
            }}
            data-testid={`card-decision-description-${itemId}`}
          >
            {questionsData.card_description.split(/\n{2,}/).map((para, idx) => (
              <p
                key={idx}
                className="text-sm leading-relaxed"
                style={{ color: "hsl(var(--foreground))", whiteSpace: "pre-wrap" }}
              >
                {renderInlineBold(para)}
              </p>
            ))}
          </div>
        )}

        {questions.map((q) => (
          <QuestionBlock
            key={q.number}
            question={q}
            qState={picks[q.number] || { letters: [], free_text: "" }}
            onTogglePick={(letter) =>
              togglePick(q.number, letter, q.pick_type === "checkbox")
            }
            onFreeText={(text) => setFreeTextFor(q.number, text)}
            itemId={itemId}
          />
        ))}
      </div>

      <div className="mb-3">
        <div className="flex items-center gap-1 mb-1">
          <PencilSimple
            size={10}
            style={{ color: "hsl(var(--muted-foreground))" }}
          />
          <label
            className="text-mono"
            style={{
              fontSize: "0.6rem",
              color: "hsl(var(--muted-foreground))",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 600,
            }}
            htmlFor={`card-cross-notes-${itemId}`}
          >
            Cross-question notes (optional)
          </label>
        </div>
        <textarea
          id={`card-cross-notes-${itemId}`}
          value={crossQuestionNotes}
          onChange={(e) => setCrossQuestionNotes(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          rows={2}
          placeholder="Anything that spans multiple questions, or context MP should know overall."
          className="w-full rounded-md border px-2 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{
            fontSize: "0.75rem",
            background: "hsl(var(--background))",
            borderColor: "hsl(var(--border))",
            color: "hsl(var(--foreground))",
            resize: "vertical",
          }}
          data-testid={`card-decision-cross-notes-${itemId}`}
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-2">
        <span
          className="text-mono"
          style={{
            fontSize: "0.65rem",
            color: allAnswered
              ? "hsl(var(--status-success))"
              : anyAnswered
                ? "hsl(var(--status-warning))"
                : "hsl(var(--muted-foreground))",
            fontWeight: 600,
          }}
          data-testid={`card-decision-status-${itemId}`}
        >
          {allAnswered
            ? `All ${questions.length} questions answered`
            : anyAnswered
              ? `${answeredCount} of ${questions.length} answered (partial OK)`
              : "Answer ≥1 question (pick or note) to enable submit"}
        </span>
        <div className="flex items-center gap-2 sm:gap-3 justify-end">
          {(anyAnswered || crossQuestionNotes.trim()) && (
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center text-mono transition-colors hover:text-[hsl(var(--foreground))]"
              style={{
                fontSize: "0.65rem",
                color: "hsl(var(--muted-foreground))",
                fontWeight: 600,
              }}
              data-testid={`card-cancel-${itemId}`}
            >
              Reset
            </button>
          )}
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-1.5 rounded-md px-4 sm:px-3 py-2 sm:py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring flex-1 sm:flex-initial"
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "hsl(var(--primary-foreground))",
              background: canSubmit ? "hsl(var(--primary))" : "hsl(var(--muted))",
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
            data-testid={`card-submit-${itemId}`}
          >
            {submitting ? (
              <>
                <Clock size={11} />
                Submitting…
              </>
            ) : (
              <>
                <PaperPlaneTilt size={11} />
                Commit decision
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
